import { useEffect, useMemo, useRef } from 'react';
import { useHistory, useParams } from 'react-router';
import { Form, Formik, FormikProps } from 'formik';
import { useStore } from 'react-redux';
import { Store } from 'redux';
import { useAppSelector } from '../../effects/useAppSelector';
import { useAppDispatch } from '../../effects/useAppDispatch';
import { useArrangers } from '../../effects/data-accessors/useArrangers';
import { useCollateralManagers } from '../../effects/data-accessors/useCollateralManagers';
import { useDebtInvestors } from '../../effects/data-accessors/useDebtInvestors';
import { useLoanFieldValues } from '../../effects/data-accessors/useLoanFieldValues';
import { useStoreLoanOrFetch } from '../../effects/data-accessors/useStoreLoanOrFetch';
import { isRequesting, isRequestNone } from '../../utils';
import { routes } from '../../constants';
import { loanActions } from '../../actions/loan.actions';
import { LoanStatus } from '../../types/loan/LoanStatus';
import { LoanCurrency } from '../../types/loan/LoanCurrency';
import { DataroomProvider, dataroomProviderTitles } from '../../types/enums/loan/DataroomProvider';
import {
    LoanFitchRating,
    LoanMoodysRating,
    LoanSnpRating,
    loanFitchRatingTitles,
    loanMoodysRatingTitles,
    loanSnpRatingTitles,
} from '../../types/enums/loan/LoanRatings';
import { SaveLoanContactModel } from '../../types/loan/SaveLoanModel';
import { Preloader } from '../common/Preloader';
import { Breadcrumbs } from '../controls/breadcrumbs/Breadcrumbs';
import { BreadcrumbsItem } from '../controls/breadcrumbs/BreadcrumbsItem';
import { LoanStatusLabel } from './LoanStatusLabel';
import { SectionContent } from '../amrPipeline/common/section/SectionContent';
import { TextField } from '../common/form-fields/TextField';
import { TextareaField } from '../common/form-fields/TextareaField';
import { SelectField } from '../common/form-fields/SelectField';
import { NumberField } from '../common/form-fields/NumberField';
import { DateField } from '../common/form-fields/DateField';
import { EditorField } from '../common/form-fields/EditorField';
import { RouteLeavingGuard } from '../routing';
import { gridActions } from '../../actions';
import { DropDownFieldWithAddNew } from './edit/DropDownFieldWithAddNew';
import { DropDownField } from './edit/DropDownField';
import { CheckboxRowField } from './edit/CheckboxRowField';
import { MultiSelectField } from '../forms/MultiSelectField';
import { MultiSelectFieldWithAddNew } from '../forms/MultiSelectFieldWithAddNew';
import { TimeField } from './edit/TimeField';
import { DealContactsSection } from './edit/DealContactsSection';
import { NotFound } from '../errors/NotFound';
import { loanValidationSchema } from './edit/LoanValidationSchema';
import { buildInitialLoanFormValues, buildSavePayload, LoanFormValues } from './edit/loanEdit.utils';
import { AppState } from '../../types/state/AppState';
import { GridDataItem } from '../../types/state/GridState';
import { MultiSelectDataItem } from '../controls';

const currencyOptions = Object.values(LoanCurrency).map(c => ({ value: c, label: c }));
const statusOptions = Object.values(LoanStatus).map(s => ({ value: s, label: s }));
const dataroomProviderOptions = Object.values(DataroomProvider).map(p => ({ value: p, label: dataroomProviderTitles[p] }));
const moodysRatingOptions = Object.values(LoanMoodysRating).map(r => ({ value: r, label: loanMoodysRatingTitles[r] }));
const snpRatingOptions = Object.values(LoanSnpRating).map(r => ({ value: r, label: loanSnpRatingTitles[r] }));
const fitchRatingOptions = Object.values(LoanFitchRating).map(r => ({ value: r, label: loanFitchRatingTitles[r] }));

export function EditLoanView() {
    const { loanId } = useParams<{ loanId?: string }>();
    const numericLoanId = loanId ? Number(loanId) : undefined;
    const isEdit = numericLoanId != null && !Number.isNaN(numericLoanId);

    const history = useHistory();
    const dispatch = useAppDispatch();
    const store = useStore() as Store<AppState>;
    const formRef = useRef<FormikProps<LoanFormValues>>(null);
    const saveRequestState = useAppSelector(s => s.loan.saveRequestState);
    const isSaving = isRequesting(saveRequestState);

    useEffect(() => {
        return () => {
            dispatch(loanActions.saveLoanReset());
        };
    }, [dispatch]);

    const { items: arrangers, requestState: arrangersRequestState } = useArrangers();
    const { items: collateralManagers, requestState: collateralManagersRequestState } = useCollateralManagers();
    const { items: debtInvestors, requestState: debtInvestorsRequestState } = useDebtInvestors();
    const { values: allFieldValues, requestState: fieldValuesRequestState } = useLoanFieldValues();
    const { loan, loanRequestState } = useStoreLoanOrFetch(numericLoanId);

    const gridDataItems = useAppSelector(s => s.grid.dataItems);

    const initialValues = useMemo(
        () => buildInitialLoanFormValues(isEdit ? loan : undefined),
        [isEdit, loan],
    );

    const isInitialDataLoading =
        isRequesting(arrangersRequestState, collateralManagersRequestState, debtInvestorsRequestState, fieldValuesRequestState) ||
        isRequestNone(arrangersRequestState, collateralManagersRequestState, debtInvestorsRequestState, fieldValuesRequestState);
    const isLoanLoading = isEdit && !loan && (isRequesting(loanRequestState) || isRequestNone(loanRequestState));
    const isReady = !isInitialDataLoading && !isLoanLoading;

    const arrangerOptions = useMemo(() => {
        const companies = [
            ...Object.values(arrangers || {}),
            ...Object.values(collateralManagers || {}),
            ...Object.values(debtInvestors || {}),
        ];
        // Dedupe by referenceName in case a company holds more than one role
        const uniqueByReferenceName = new Map(companies.map(a => [a.referenceName, a]));

        return Array
            .from(uniqueByReferenceName.values())
            .map(a => ({ value: a.referenceName, label: a.legalName }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [arrangers, collateralManagers, debtInvestors]);

    const issuerOptions = allFieldValues?.issuers || [];
    const facilityOptions = allFieldValues?.facilities || [];
    const industryFromStore = allFieldValues?.industries || [];
    const refIndexFromStore = allFieldValues?.refIndexes || [];
    const useOfProceedsFromStore = allFieldValues?.useOfProceeds || [];

    const collectContacts = (): SaveLoanContactModel[] => {
        return (gridDataItems as GridDataItem<SaveLoanContactModel>[])
            .filter(item => !item.draft)
            .map(item => ({
                id: item.id ?? null,
                firstName: item.firstName,
                lastName: item.lastName,
                desk: item.desk,
                title: item.title,
                email: item.email,
                officePhone: item.officePhone,
                mobilePhone: item.mobilePhone,
                linkedIn: item.linkedIn,
            }));
    };

    const submitWithStatus = (values: LoanFormValues, status: LoanStatus) => {
        const hasContacts = gridDataItems.some(x => !x.draft);
        
        if(hasContacts){
            dispatch(gridActions.validate());
            const isValid = store.getState().grid.isValid;
            if (!isValid) return;
        }

        const contacts = hasContacts ? collectContacts() : [];
        const payload = buildSavePayload(values, contacts, status);
        dispatch(loanActions.adminLoanSave(isEdit ? numericLoanId : undefined, payload));
    };

    if (!isReady) {
        return (
            <div className="container loan-edit">
                <Preloader inProgress />
            </div>
        );
    }

    if (isEdit && !loan) {
        return <NotFound />;
    }

    const generateSelectedText = (items: MultiSelectDataItem[]) => (
        <div className="multiselect-row">
            <span className="text-ellipsis">{items[0].text}</span>
            <span>{items.length > 1 && (' and ' + (items.length - 1) + ' more')}</span>
        </div>
    )

    return (
        <Formik<LoanFormValues>
            initialValues={initialValues}
            innerRef={formRef}
            validationSchema={loanValidationSchema}
            enableReinitialize
            onSubmit={() => undefined}
        >
            {({ values, dirty, isValid, setFieldValue, validateForm, setTouched }) => {
                const isPublished = isEdit && loan?.status && loan.status !== LoanStatus.Draft;

                const triggerValidate = async () => {
                    const errors = await validateForm();
                    setTouched(
                        Object.keys(errors).reduce<Record<string, boolean>>((acc, k) => {
                            acc[k] = true;
                            return acc;
                        }, {}),
                        true,
                    );
                    return Object.keys(errors).length === 0;
                };

                const handleSaveDraft = async () => {
                    setFieldValue('status', LoanStatus.Draft);
                    if (await triggerValidate()) {
                        submitWithStatus({ ...values, status: LoanStatus.Draft }, LoanStatus.Draft);
                    }
                };

                const handleSave = async () => {
                    if (await triggerValidate()) {
                        submitWithStatus(values, values.status || LoanStatus.Active);
                    }
                };

                const handlePublish = async () => {
                    const newStatus = values.status && values.status !== LoanStatus.Draft
                        ? values.status
                        : LoanStatus.Active;

                    if (await triggerValidate()) {
                        setFieldValue('status', newStatus);
                        submitWithStatus({ ...values, status: newStatus }, newStatus);
                    }
                };

                const handleUnpublish = () => {
                    setFieldValue('status', LoanStatus.Draft);
                    submitWithStatus({ ...values, status: LoanStatus.Draft }, LoanStatus.Draft);
                };

                const titleText = isEdit
                    ? loan?.issuerName || 'Edit Loan'
                    : 'Create New Loan';

                const hasGridErrors = gridDataItems.some(item => !item.draft && !!item.errors?.length);
                const saveDraftDisabled = isSaving || !isValid || hasGridErrors || values.status !== LoanStatus.Draft;
                const saveDisabled = isSaving || !isValid || hasGridErrors;

                return (
                    <Form className="container loan-edit">
                        <RouteLeavingGuard
                            navigate={(pathname: string) => history.push(pathname)}
                            shouldBlockNavigation={() => !!dirty && !isSaving}
                        />
                        <div className="sub-header loan-edit-header">
                            <div className="sub-header-row type04 flex-row">
                                <Breadcrumbs>
                                    <BreadcrumbsItem text="Loans" route={routes.loanMonitor} />
                                </Breadcrumbs>
                            </div>
                            <div className="sub-header-row type04 flex-row loan-edit-title-row">
                                <h1>{titleText}</h1>
                                <LoanStatusLabel status={values.status || LoanStatus.Draft} />
                                <div className="flex-item-right flex-row loan-edit-actions">
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm"
                                        onClick={() => history.push(routes.loanMonitor)}
                                        disabled={isSaving}
                                    >
                                        Cancel
                                    </button>
                                    {isPublished ? (
                                        <>
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-sm"
                                                onClick={handleUnpublish}
                                                disabled={saveDisabled}
                                            >
                                                Unpublish
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-main btn-sm"
                                                onClick={handleSave}
                                                disabled={saveDisabled}
                                            >
                                                Save
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-sm"
                                                onClick={handleSaveDraft}
                                                disabled={saveDraftDisabled}
                                            >
                                                Save Draft
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-main btn-sm"
                                                onClick={handlePublish}
                                                disabled={saveDisabled}
                                            >
                                                Publish
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="tabs-data tabs-data-padding loan-edit-content">
                            <div className="data-item-row-wrap">
                                <div className="data-item-row-col">
                                    <SectionContent title="Parties">
                                        <DropDownFieldWithAddNew
                                            name="issuerName"
                                            label="Issuer"
                                            options={issuerOptions.map(o => ({ value: o, text: o }))}
                                            placeholder="Select"
                                            required
                                            maxLength={64}
                                            wrapperClassName="data-item-row-title-md"
                                            withSearch
                                        />
                                        <DropDownField
                                            label="Arranger"
                                            name="arrangerReferenceName"
                                            options={arrangerOptions.map(o => ({ value: o.value, text: o.label }))}
                                            placeholder="Select"
                                            wrapperClassName="data-item-row-title-md"
                                            withEmptyValue
                                            withSearch
                                        />
                                        <MultiSelectField
                                            name="coArrangers"
                                            label="Co-Arranger"
                                            dataItems={arrangerOptions.map(o => ({ value: o.value, text: o.label }))}
                                            placeholder="Select"
                                            wrapperClassName="data-item-row-title-md"
                                            horizontalOrientation
                                            onGenerateSelectedText={generateSelectedText}
                                            withSearch
                                        />
                                        <h3 className="loan-edit-subsection-title">Data Room</h3>
                                        <SelectField
                                            label="Provider"
                                            name="dataroomProvider"
                                            values={dataroomProviderOptions}
                                            placeholder="Select"
                                            optionKeyProp="value"
                                            optionLabelProp="label"
                                            withEmptyValue
                                            wrapperClassName="data-item-row-title-md"
                                        />
                                        <TextareaField
                                            label="Link"
                                            name="dataroomLink"
                                            maxLength={2048}
                                            placeholder="https://"
                                            wrapperClassName="data-item-row-title-md data-item-row-full-height"
                                        />
                                    </SectionContent>
                                </div>
                                <div className="data-item-row-col">
                                    <SectionContent title="Dates">
                                        <DateField
                                            label="Due Date"
                                            name="dueDate"
                                            placeholder="MM/DD/YYYY"
                                            emptyIfNull
                                            showErrorWhenUntouched
                                            wrapperClassName="data-item-row-title-md"
                                        />
                                        <TimeField
                                            label="Due Time (EST)"
                                            name="dueTime"
                                            wrapperClassName="data-item-row-title-md"
                                        />
                                        <DateField
                                            label="Maturity"
                                            name="maturity"
                                            placeholder="MM/DD/YYYY"
                                            emptyIfNull
                                            wrapperClassName="data-item-row-title-md"
                                        />
                                        <NumberField
                                            label="Term, yrs"
                                            name="termYears"
                                            placeholder="yy.yy"
                                            includeThousandsSeparator={false}
                                            wrapperClassName="data-item-row-title-md"
                                            capitalize={false}
                                            decimalLimit={2}
                                        />
                                        <DateField
                                            label="Closing"
                                            name="closingDate"
                                            placeholder="MM/DD/YYYY"
                                            emptyIfNull
                                            wrapperClassName="data-item-row-title-md"
                                        />
                                        <TextField
                                            label="Call Protection"
                                            name="callProtection"
                                            maxLength={256}
                                            placeholder="e.g. 101 soft call for 12 months"
                                            wrapperClassName="data-item-row-title-md"
                                        />
                                    </SectionContent>
                                </div>
                                <div className="data-item-row-col">
                                    <SectionContent title="Lender Call">
                                        <DateField
                                            label="Date"
                                            name="lenderCallDate"
                                            placeholder="MM/DD/YYYY"
                                            emptyIfNull
                                            wrapperClassName="data-item-row-title-md"
                                        />
                                        <TimeField
                                            label="Time (EST)"
                                            name="lenderCallTime"
                                            wrapperClassName="data-item-row-title-md"
                                        />

                                        <h3 className="loan-edit-subsection-title">Live Dial-in</h3>
                                        <TextField
                                            label="Domestic"
                                            name="lenderCallLiveDomestic"
                                            maxLength={64}
                                            placeholder="000-000-0000"
                                            wrapperClassName="data-item-row-title-md"
                                            formControlClassName="text-right"
                                        />
                                        <TextField
                                            label="International"
                                            name="lenderCallLiveInternational"
                                            maxLength={64}
                                            placeholder="000-000-0000"
                                            wrapperClassName="data-item-row-title-md"
                                            formControlClassName="text-right"
                                        />
                                        <NumberField
                                            label="Passcode"
                                            name="lenderCallLivePasscode"
                                            includeThousandsSeparator={false}
                                            placeholder="0000"
                                            wrapperClassName="data-item-row-title-md"
                                        />

                                        <h3 className="loan-edit-subsection-title">Replay Dial-in</h3>
                                        <TextField
                                            label="Domestic"
                                            name="lenderCallReplayDomestic"
                                            maxLength={64}
                                            placeholder="000-000-0000"
                                            wrapperClassName="data-item-row-title-md"
                                            formControlClassName="text-right"
                                        />
                                        <TextField
                                            label="International"
                                            name="lenderCallReplayInternational"
                                            maxLength={64}
                                            placeholder="000-000-0000"
                                            wrapperClassName="data-item-row-title-md"
                                            formControlClassName="text-right"
                                        />
                                        <NumberField
                                            label="Passcode"
                                            name="lenderCallReplayPasscode"
                                            includeThousandsSeparator={false}
                                            placeholder="0000"
                                            wrapperClassName="data-item-row-title-md"
                                        />
                                    </SectionContent>
                                </div>
                            </div>

                            <SectionContent title="Information">
                                <div className="data-item-row-wrap">
                                    <div className="data-item-row-col">
                                        <SelectField
                                            label="Status"
                                            name="status"
                                            values={statusOptions}
                                            placeholder="Select"
                                            optionKeyProp="value"
                                            optionLabelProp="label"
                                            required
                                            wrapperClassName="data-item-row-title-md"
                                        />
                                        <DropDownFieldWithAddNew
                                            name="facility"
                                            label="Facility"
                                            options={facilityOptions.map(o => ({ value: o, text: o }))}
                                            placeholder="Select"
                                            maxLength={256}
                                            withEmptyValue
                                            withSearch
                                            wrapperClassName="data-item-row-title-md"
                                        />
                                        <MultiSelectFieldWithAddNew
                                            name="useOfProceeds"
                                            label="Use of Proceeds"
                                            dataItems={useOfProceedsFromStore.map(o => ({ value: o, text: o }))}
                                            placeholder="Select"
                                            wrapperClassName="data-item-row-title-md"
                                            onGenerateSelectedText={generateSelectedText}
                                            showResetButton={true}
                                            withSearch
                                        />
                                        <NumberField
                                            label="Size, MM"
                                            name="size"
                                            placeholder="xx.xxx"
                                            wrapperClassName="data-item-row-title-md"
                                            decimalLimit={3}
                                            autoFormat
                                            includeThousandsSeparator={true}
                                            allowNegative={false}
                                        />
                                        <NumberField
                                            label="Add-on, MM"
                                            name="addOn"
                                            placeholder="xx.xxx"
                                            wrapperClassName="data-item-row-title-md"
                                            decimalLimit={3}
                                            autoFormat
                                            includeThousandsSeparator={true}
                                            allowNegative={false}
                                        />
                                    </div>
                                    <div className="data-item-row-col">
                                        <SelectField
                                            label="Currency"
                                            name="currencyCode"
                                            values={currencyOptions}
                                            placeholder="Select"
                                            optionKeyProp="value"
                                            optionLabelProp="label"
                                            withEmptyValue
                                            wrapperClassName="data-item-row-title-md"
                                        />
                                        <CheckboxRowField label="Cov-Lite" name="covLite" wrapperClassName="data-item-row-title-md" />
                                        <DropDownFieldWithAddNew
                                            name="industry"
                                            label="Industry"
                                            options={industryFromStore.map(o => ({ value: o, text: o }))}
                                            placeholder="Select"
                                            maxLength={64}
                                            withEmptyValue
                                            withSearch
                                            wrapperClassName="data-item-row-title-md"
                                        />
                                        <TextField
                                            label="FIGI"
                                            name="figi"
                                            maxLength={64}
                                            wrapperClassName="data-item-row-title-md"
                                        />
                                        <TextField
                                            label="Bbmg Id"
                                            name="bbmgId"
                                            maxLength={64}
                                            wrapperClassName="data-item-row-title-md"
                                        />
                                        <TextField
                                            label="LoanX Id"
                                            name="loanXId"
                                            maxLength={64}
                                            wrapperClassName="data-item-row-title-md"
                                        />
                                    </div>
                                    <div className="data-item-row-col">
                                        <div className="data-item-row data-item-row-title-md loan-edit-guidance-row">
                                            <label className="data-item-col data-item-col-title">Guidance</label>
                                            <div className="data-item-col loan-edit-guidance-inputs">
                                                <NumberField
                                                    label=""
                                                    name="guidanceMin"
                                                    placeholder="Min"
                                                    includeThousandsSeparator={false}
                                                    integerLimit={3}
                                                    decimalLimit={1}
                                                />
                                                <NumberField
                                                    label=""
                                                    name="guidanceMax"
                                                    placeholder="Max"
                                                    includeThousandsSeparator={false}
                                                    integerLimit={3}
                                                    decimalLimit={1}
                                                />
                                            </div>
                                        </div>
                                        <div className="data-item-row data-item-row-title-md loan-edit-guidance-row">
                                            <label className="data-item-col data-item-col-title">Price</label>
                                            <div className="data-item-col loan-edit-guidance-inputs">
                                                <NumberField
                                                    label=""
                                                    name="priceMin"
                                                    placeholder="Min"
                                                    includeThousandsSeparator={false}
                                                    integerLimit={3}
                                                    decimalLimit={3}
                                                    autoFormat
                                                />
                                                <NumberField
                                                    label=""
                                                    name="priceMax"
                                                    placeholder="Max"
                                                    includeThousandsSeparator={false}
                                                    integerLimit={3}
                                                    decimalLimit={3}
                                                    autoFormat
                                                />
                                            </div>
                                        </div>
                                        <NumberField
                                            label="Floor, %"
                                            name="floor"
                                            placeholder="xx.xx"
                                            includeThousandsSeparator={false}
                                            wrapperClassName="data-item-row-title-md"
                                            integerLimit={3}
                                            decimalLimit={2}
                                            autoFormat
                                        />
                                        <NumberField
                                            label="Margin"
                                            name="margin"
                                            placeholder="xx.xxx"
                                            includeThousandsSeparator={false}
                                            wrapperClassName="data-item-row-title-md"
                                            integerLimit={3}
                                            decimalLimit={3}
                                            autoFormat
                                        />
                                        <DropDownFieldWithAddNew
                                            name="refIndex"
                                            label="Reference Index"
                                            options={refIndexFromStore.map(o => ({ value: o, text: o }))}
                                            placeholder="Select"
                                            maxLength={64}
                                            withEmptyValue
                                            withSearch
                                            wrapperClassName="data-item-row-title-md"
                                        />
                                    </div>
                                </div>
                                <EditorField
                                    label="Issuer Description"
                                    name="issuerDescription"
                                    maxLength={2048}
                                    wrapperClassName="data-item-row-title-md-2 data-item-row-full-height loan-edit-issuer-description"
                                    limitHTML={true}
                                    countHTMLChars={true}
                                />
                                <EditorField
                                    label="Use of Proceeds Description"
                                    name="useOfProceedsDescription"
                                    maxLength={2048}
                                    wrapperClassName="data-item-row-title-md-2 data-item-row-full-height loan-edit-issuer-description"
                                    limitHTML={true}
                                    countHTMLChars={true}
                                />
                            </SectionContent>
                            <SectionContent title="Ratings" className="loan-edit-ratings-section">
                                <div className="data-item-row-wrap">
                                    <div className="data-item-row-col">
                                        <h3 className="loan-edit-subsection-title">Tranche Rating</h3>
                                        <DropDownField
                                            label="Moody's"
                                            name="trancheRatingMoodys"
                                            options={moodysRatingOptions.map(r => ({ value: r.value, text: r.label }))}
                                            placeholder="Select"
                                            wrapperClassName="data-item-row-title-md"
                                            withSearch
                                            withEmptyValue
                                        />
                                        <DropDownField
                                            label="S&P"
                                            name="trancheRatingSnP"
                                            options={snpRatingOptions.map(r => ({ value: r.value, text: r.label }))}
                                            placeholder="Select"
                                            wrapperClassName="data-item-row-title-md"
                                            withSearch
                                            withEmptyValue
                                        />
                                        <DropDownField
                                            label="Fitch"
                                            name="trancheRatingFitch"
                                            options={fitchRatingOptions.map(r => ({ value: r.value, text: r.label }))}
                                            placeholder="Select"
                                            wrapperClassName="data-item-row-title-md"
                                            withSearch
                                            withEmptyValue
                                        />
                                    </div>
                                    <div className="data-item-row-col">
                                        <h3 className="loan-edit-subsection-title">Credit Facility Rating</h3>
                                        <DropDownField
                                            label="Moody's"
                                            name="cfRatingMoodys"
                                            options={moodysRatingOptions.map(r => ({ value: r.value, text: r.label }))}
                                            placeholder="Select"
                                            wrapperClassName="data-item-row-title-md"
                                            withSearch
                                            withEmptyValue
                                        />
                                        <DropDownField
                                            label="S&P"
                                            name="cfRatingSnP"
                                            options={snpRatingOptions.map(r => ({ value: r.value, text: r.label }))}
                                            placeholder="Select"
                                            wrapperClassName="data-item-row-title-md"
                                            withSearch
                                            withEmptyValue
                                        />
                                        <DropDownField
                                            label="Fitch"
                                            name="cfRatingFitch"
                                            options={fitchRatingOptions.map(r => ({ value: r.value, text: r.label }))}
                                            placeholder="Select"
                                            wrapperClassName="data-item-row-title-md"
                                            withSearch
                                            withEmptyValue
                                        />
                                    </div>
                                    <div className="data-item-row-col" />
                                </div>
                            </SectionContent>

                            <DealContactsSection initialContacts={loan?.contacts ?? []} />
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
}

